<template>
  <div class="d-content">
    <div class="main-container">
    
    <div v-if="isLoading" class="loading-container">
      <tl-loading />
    </div>
    
    <div v-else>
      <div class="margintop-32" />
      <tl-header type="h1">Letter Management</tl-header>
      
      <!-- Menu Tabs -->
      <div class="menu-tabs marginbottom-32">
        <button 
          class="menu-tab" 
          :class="{ active: activeTab === 'generate' }"
          @click="activeTab = 'generate'"
        >
          Generate Letters
        </button>
        <button 
          class="menu-tab" 
          :class="{ active: activeTab === 'bulk' }"
          @click="activeTab = 'bulk'"
        >
          Bulk Download
        </button>
      </div>

      <!-- Generate Letters Tab -->
      <div v-if="activeTab === 'generate'" class="tab-content">
        <!-- Template Selection -->
        <div class="filter-container marginbottom-32">
          <div class="filter-row">
            <div class="filter-item">
              <tl-select
                v-model="selectedTemplate"
                :options="templateOptions"
                label="Select Letter Template"
                labeltext="display_name"
                @change="onTemplateChange"
                placeholder="Choose a template"
              />
            </div>
          </div>
        </div>
        
        <!-- Template Actions -->
        <div v-if="selectedTemplate && selectedTemplate.id" class="template-actions marginbottom-32">
          <div class="action-row">
            <!-- Download Template Button -->
            <btn
              v-if="templateDetails && templateDetails.csvDownloadUrl"
              type="secondary"
              text="Download Template"
              @click="showDownloadTemplatePopup"
            />
            
            <!-- Upload File Section -->
            <div class="upload-section">
              <upload
                ref="csvUploadRef"
                name="csvUpload"
                accept=".csv"
                :tobucket="false"
                @change="onCsvUpload"
              />
            </div>
            
            <!-- Upload & Process Button -->
            <btn
              type="primary"
              text="Upload & Process"
              @click="showUploadConfirmation"
              :disabled="!uploadedFile || isProcessing"
            />
          </div>
        </div>

        <!-- Generate Letters Data Table -->
        <div v-if="selectedTemplate && templateDetails" class="results-container">
          <div v-if="templateDetails.requests && templateDetails.requests.length === 0" class="no-records-message">
            <p>No record found for the selected Template</p>
          </div>
        <div v-else-if="templateDetails.requests && templateDetails.requests.length > 0">
          <div class="results-header marginbottom-16">
            <div class="results-header-content">
              <tl-header type="h2">Generated Letters Requests</tl-header>
              <btn
                type="primary"
                text="Process Selected"
                @click="showProcessSelectedConfirmation"
                :disabled="!processSelectedLettersToggle"  
              />
              <!-- || !processSelectedLettersToggle -->
            </div>
          </div>

          <data-table
            :columns="generateLettersColumns"
            :tableData="templateDetails.requests"
            :with-form="false"
            :showPaging="false"
            :showinrow="false"
            :checkbox="true"
            @selected="onRequestsSelected"
            @headerSelection="onHeaderSelection"
            class="letters-table"
          >
            <template #rowcontent="{ value }">
              <div v-if="value.column.key === 'employee_code'">
                {{ value.record.data.employee_code }}
              </div>
              <div v-else-if="value.column.key === 'status'">
                <span class="status-badge" :class="value.record.status">
                  {{ value.record.status }}
                </span>
              </div>
              <div v-else-if="value.column.key === 'template_name'">
                {{ `${templateDetails.template.code}-${templateDetails.template.name}`}}
              </div>
              <div v-else-if="value.column.key === 'updated_date'">
                {{ formatDate(value.record.updated_date) }}
              </div>
              <div v-else-if="value.column.key === 'actions'" class="table-actions">
                <btn
                  type="secondary"
                  text="Delete"
                  class="table-btn"
                  @click="showDeleteConfirmation(value.record)"
                  :disabled="isProcessing"
                />
                <btn
                  type="primary"
                  text="Process"
                  class="table-btn"
                  @click="showProcessConfirmation(value.record)"
                  :disabled="isProcessing"
                />
              </div>
              <div v-else>
                {{ value.record[value.column.key] }}
              </div>
            </template>
          </data-table>
        </div>
        </div>
      </div>

      <!-- Bulk Download Tab -->
      <div v-if="activeTab === 'bulk'" class="tab-content">
        <!-- Filter Section -->
        <div class="filter-container marginbottom-32">
          <div class="filter-row">
            <div class="filter-item filter-year">
              <tl-select
                v-model="bulkSelectedYear"
                :options="yearOptions"
                label="Select Year"
                labeltext="label"
                placeholder="Choose year"
                @change="(val) => bulkSelectedYear = val"
              />
            </div>
            
            <div class="filter-item">
              <tl-select
                v-model="bulkSelectedTemplate"
                :options="templateOptions"
                label="Select Letter Template"
                labeltext="display_name"
                placeholder="Choose template"
                @change="(val) => bulkSelectedTemplate = val"
              />
            </div>
            
            <div class="filter-item">
              <txt
                v-model="bulkSearchEmployee"
                type="text"
                label="Employee Code"
                placeholder="Enter employee code"
                @change="(val) => bulkSearchEmployee = val"
              />
            </div>
            
            <div class="filter-item filter-actions">
              <btn
                type="primary"
                text="Search Letters"
                @click="fetchBulkLetters"
                :disabled="isFetching"
              />
            </div>
          </div>
        </div>

        <!-- Bulk Download Results -->
        <div v-if="bulkDownloadResponse" class="results-container">
          <div class="results-header marginbottom-16">
            <div class="results-header-content">
              <tl-header type="h2">Available Letters ({{ bulkDownloadResponse.totalLetters || 0 }})</tl-header>
              <btn
                v-if="bulkDownloadResponse.downloadUrls && bulkDownloadResponse.downloadUrls.length > 0"
                type="primary"
                text="Zip Download"
                @click="showZipConfirmation"
                :disabled="isDownloading"
              />
            </div>
          </div>

          <div v-if="bulkDownloadResponse.downloadUrls && bulkDownloadResponse.downloadUrls.length === 0" class="no-records-message">
            <p>No record found for the selected Template</p>
          </div>
          
          <data-table
            v-if="bulkDownloadResponse.downloadUrls && bulkDownloadResponse.downloadUrls.length > 0"
            :columns="bulkDownloadColumns"
            :tableData="bulkDownloadResponse.downloadUrls"
            :with-form="false"
            :showPaging="false"
            :showinrow="false"
            class="letters-table"
          >
            <template #rowcontent="{ value }">
              <div v-if="value.column.key === 'downloadUrl'">
                <div class="download-container">
                  <button 
                    @click="downloadSingleFile(value.record)" 
                    class="download-btn"
                    :disabled="downloadingFiles[value.record.id]"
                  >
                    <div v-if="downloadingFiles[value.record.id]" class="download-progress">
                      <div class="spinner"></div>
                      <span>{{ downloadProgress[value.record.id] }}</span>
                    </div>
                    <div v-else class="download-content" style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                      <img src="@/assets/download.svg" class="download-icon" />
                      <span>Download</span>
                    </div>
                  </button>
                </div>
              </div>
              <div v-else-if="value.column.key === 'generatedAt'">
                {{ formatDate(value.record.generatedAt) }}
              </div>
              <div v-else>
                {{ value.record[value.column.key] }}
              </div>
            </template>
          </data-table>
        </div>
      </div>
    </div>

    <!-- Toast Messages -->
    <toast-message
      v-if="toastMessage.show"
      :message="toastMessage.text"
      :type="toastMessage.type"
      @close="closeToast"
    />
    
    <!-- Zip Download Confirmation Popup -->
    <pop-up-message v-if="showZipConfirmModal" 
      message="Are you sure you want to download all letters as a ZIP file?" 
      popupType="ConfirmDelete"
      popupHeaderMessage="Confirm Download">
      <template v-slot:bottomContent>
        <btn-grp>
          <popup-btn text="Cancel" type="secondary" @click="showZipConfirmModal = false" />
          <popup-btn text="Download" type="primary" @click="confirmZipDownload" :disabled="isConfirmingZipDownload" />
        </btn-grp>
      </template>
    </pop-up-message>
    
    <!-- Process Request Confirmation Popup -->
    <pop-up-message v-if="showProcessConfirmModal" 
      message="Are you sure you want to process this request?" 
      popupType="ConfirmDelete"
      popupHeaderMessage="Confirm Process">
      <template v-slot:bottomContent>
        <btn-grp>
          <popup-btn text="Cancel" type="secondary" @click="showProcessConfirmModal = false" />
          <popup-btn text="Confirm" type="primary" @click="confirmProcessRequest" :disabled="isConfirmingProcess" />
        </btn-grp>
      </template>
    </pop-up-message>
    
    <!-- Process Selected Requests Confirmation Popup -->
    <pop-up-message v-if="showProcessSelectedConfirmModal" 
      message="Are you sure you want to process selected requests?" 
      popupType="ConfirmDelete"
      popupHeaderMessage="Confirm Process Selected">
      <template v-slot:bottomContent>
        <btn-grp>
          <popup-btn text="Cancel" type="secondary" @click="showProcessSelectedConfirmModal = false" />
          <popup-btn text="Confirm" type="primary" @click="confirmProcessSelectedRequests" :disabled="isConfirmingProcessSelected" />
        </btn-grp>
      </template>
    </pop-up-message>
    
    <!-- Delete Request Confirmation Popup -->
    <pop-up-message v-if="showDeleteConfirmModal" 
      message="Are you sure you want to delete this request?" 
      popupType="ConfirmDelete"
      popupHeaderMessage="Confirm Delete">
      <template v-slot:bottomContent>
        <btn-grp>
          <popup-btn text="Cancel" type="secondary" @click="showDeleteConfirmModal = false" />
          <popup-btn text="Delete" type="primary" @click="confirmDeleteRequest" :disabled="isConfirmingDelete" />
        </btn-grp>
      </template>
    </pop-up-message>
    
    <!-- Upload Instructions Popup -->
    <div class="upload-instructions-popup">
      <pop-up-message v-if="showUploadInstructionsModal" 
        message="" 
        popupType="Success"
        popupHeaderMessage="Upload Instructions"
        dynamicWidth="30%">
        <template v-slot:bottomContent>
          <div class="upload-instructions-content">
            <div v-if="templateDetails && templateDetails.instructions" class="instructions-section">
              <div v-for="(section, key) in templateDetails.instructions.instructions" :key="key" class="instruction-group">
                <h4>{{ section.title }}</h4>
                <div v-if="section.formats" class="instruction-list">
                  <ul>
                    <li v-for="format in section.formats" :key="format">{{ format }}</li>
                  </ul>
                </div>
                <div v-if="section.rules" class="instruction-list">
                  <ul>
                    <li v-for="rule in section.rules" :key="rule">{{ rule }}</li>
                  </ul>
                </div>
                <div v-if="section.available" class="instruction-list">
                  <p v-if="section.note">{{ section.note }}</p>
                  <ul>
                    <li v-for="item in section.available" :key="item.display_name">{{ item.display_name }}</li>
                  </ul>
                </div>
                <div v-if="section.rule" class="instruction-text">
                  <p>{{ section.rule }}</p>
                </div>
              </div>
            </div>
            

            
            <!-- Button groups based on mode -->
            <div class="popup-btn-group">
              <!-- Download Template Mode -->
              <template v-if="popupMode === 'download'">
                <button class="btn-popup-secondary" type="button" @click="closeUploadInstructionsPopup">Cancel</button>
                <a v-if="templateDetails && templateDetails.csvDownloadUrl" 
                   :href="templateDetails.csvDownloadUrl" 
                   :download="`${selectedTemplate.code}_template.csv`" 
                   class="btn-popup download-template-btn"
                   @click="closeUploadInstructionsPopup">
                  <img src="@/assets/download.svg" class="download-icon" />
                  <span>Download Template</span>
                </a>
              </template>
              
              <!-- Upload & Process Mode -->
              <template v-if="popupMode === 'upload'">
                <button class="btn-popup-secondary" type="button" @click="closeUploadInstructionsPopup">OK</button>
              </template>
            </div>
          </div>
        </template>
      </pop-up-message>
    </div>
    
    <!-- Upload CSV Confirmation Popup -->
    <pop-up-message v-if="showUploadConfirmModal" 
      message="Are you sure you want to upload this CSV file?" 
      popupType="ConfirmDelete"
      popupHeaderMessage="Confirm Upload">
      <template v-slot:bottomContent>
        <btn-grp>
          <popup-btn text="Cancel" type="secondary" @click="showUploadConfirmModal = false" />
          <popup-btn text="Confirm" type="primary" @click="confirmUpload" :disabled="isConfirmingUpload" />
        </btn-grp>
      </template>
    </pop-up-message>
    
    <!-- Success Popup -->
    <pop-up-message v-if="showSuccessPopup" 
      :message="successPopupMessage" 
      :popupType="successPopupType"
      :popupHeaderMessage="successPopupType"
      btnText="OK"
      @back="closeSuccessPopup">
      <template v-slot:bottomContent v-if="logSheetUrl">
        <div v-if="logSheetUrl" class="margintop-32 custom-container">
          <a :href="logSheetUrl" download="ErrorsLog.txt">
            <img src="@/assets/download.svg" class="img" /> 
            <span class="text">ErrorsLog.txt</span>
          </a>
        </div>
        <div class="margintop-32 marginright" style="display: flex; justify-content: end;">
          <button class="btn-popup" type="button" @click="closeSuccessPopup()">OK</button>
        </div>
      </template>
    </pop-up-message>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: 'letterManagement',
  data() {
    return {
      activeTab: 'generate',
      
      // Generate Letters Tab
      selectedTemplate: '',
      uploadedFile: null,
      generateLettersData: [],
      selectedRequests: [],
      
      // Bulk Download Tab
      bulkSearchEmployee: '',
      bulkSelectedYear: '',
      bulkSelectedTemplate: '',
      showBulkResults: false,
      
      // Download tracking
      downloadingFiles: {},
      downloadProgress: {},
      
      // Common
      templateOptions: [],
      yearOptions: [
        { label: '2025', value: '2025' },
      ],
      
      toastMessage: {
        show: false,
        text: '',
        type: 'success'
      },
      processSelectedLettersToggle: false,
      showZipConfirmModal: false,
      showProcessConfirmModal: false,
      selectedRequestToProcess: null,
      showProcessSelectedConfirmModal: false,
      showDeleteConfirmModal: false,
      selectedRequestToDelete: null,
      showUploadConfirmModal: false,
      showUploadInstructionsModal: false,
      popupMode: 'upload', // 'upload' or 'download'
      errorDownloadUrl: null,
      logSheetUrl: null,
      isConfirmingProcess: false,
      isConfirmingProcessSelected: false,
      isConfirmingDelete: false,
      isConfirmingZipDownload: false,
      showSuccessPopup: false,
      successPopupMessage: '',
      successPopupType: 'Success',
      isConfirmingUpload: false,
      
      // Generate Letters Table Columns
      generateLettersColumns: [
        {
          label: 'Employee Code',
          key: 'employee_code'
        },
        {
          label: 'Status',
          key: 'status'
        },
        {
          label: 'Template Name',
          key: 'template_name'
        },
        {
          label: 'Uploaded Date',
          key: 'updated_date'
        },
        {
          label: 'Actions',
          key: 'actions'
        }
      ],
      
      bulkDownloadColumns: [
        {
          label: 'Employee Code',
          key: 'employeeCode'
        },
        {
          label: 'Employee Name',
          key: 'employeeName'
        },
        {
          label: 'Template Name',
          key: 'templateName'
        },
        {
          label: 'Generated Date',
          key: 'generatedAt'
        },
        {
          label: 'Download',
          key: 'downloadUrl'
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      getLegalEntityId: "onboard/legal_entities/legal_entity_ID"
    }),
    ...mapState({
      isLoading: state => state.dashboard.letterManagement.isLoading,
      isFetching: state => state.dashboard.letterManagement.isFetching,
      isProcessing: state => state.dashboard.letterManagement.isProcessing,
      isDownloading: state => state.dashboard.letterManagement.isDownloading,
      templates: state => state.dashboard.letterManagement.templates,
      letters: state => state.dashboard.letterManagement.letters,
      uploadResponse: state => state.dashboard.letterManagement.uploadResponse,
      templateDetails: state => state.dashboard.letterManagement.templateDetails,
      bulkDownloadResponse: state => state.dashboard.letterManagement.bulkDownloadResponse
    }),
    leId() {
      if (this.$route.params.entity_index) {
        return this.getLegalEntityId(this.$route.params.entity_index);
      } else {
        return this.getLegalEntityId(0);
      }
    },

  },
  watch: {
    templateDetails: {
      handler(newVal) {
        if (newVal && newVal.requests) {
          this.generateLettersData = newVal.requests;
        }
      },
      deep: true,
      immediate: true
    },
    bulkDownloadResponse: {
      handler(newVal) {
        console.log('bulkDownloadResponse changed:', newVal);
        if (newVal && newVal.downloadUrls) {
          console.log('downloadUrls length:', newVal.downloadUrls.length);
        }
      },
      deep: true,
      immediate: true
    },
    // uploadResponse: {
    //   handler(newVal) {
    //     console.log('uploadResponse changed:', newVal);
    //     if (newVal && newVal.statusCode === 400) {
    //       console.log('Validation error detected in watcher');
    //     }
    //   },
    //   deep: true,
    //   immediate: true
    // }
  },
  async mounted() {
    console.log('Legal Entity ID:', this.leId);
    await this.loadTemplates();
  },

  methods: {
    ...mapActions({
      fetchTemplates: 'dashboard/letterManagement/fetchTemplates',
      getTemplateDetails: 'dashboard/letterManagement/getTemplateDetails',
      fetchLetters: 'dashboard/letterManagement/fetchLetters',
      fetchLetterRequests: 'dashboard/letterManagement/fetchLetterRequests',
      uploadCsv: 'dashboard/letterManagement/uploadCsv',
      processSelectedLetters: 'dashboard/letterManagement/processSelectedLetters',
      processLetter: 'dashboard/letterManagement/processLetter',
      deleteRequest: 'dashboard/letterManagement/deleteRequest',
      bulkDownloadZip: 'dashboard/letterManagement/bulkDownloadZip',
      bulkDownload: 'dashboard/letterManagement/bulkDownload'
    }),
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    
    async loadTemplates() {
      try {
        let res = await this.fetchTemplates(this.leId);

        this.templates.map(template => {
          template.label = template.display_name;
          template.value = template.id;
        });
        console.log(this.templates, 'modified property')
        this.templateOptions = this.templates;
        console.log('Templates loaded:', res);
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    },
    
    async onTemplateChange(selectedTemplate) {
      console.log('Template changed:', selectedTemplate);
      this.selectedTemplate = selectedTemplate;

      if (selectedTemplate && selectedTemplate.id) {
        try {
          await this.getTemplateDetails({
            templateId: selectedTemplate.id,
            leId: this.leId
          });
          // Load existing requests for this template
          await this.loadGenerateLettersData();
        } catch (error) {
          console.error('Error fetching template details:', error);
        }
      }
    },
    

    
    onCsvUpload(file) {
      this.uploadedFile = file;
    },
    
    showUploadInstructionsPopup() {
      if (!this.selectedTemplate) {
        return;
      }
      this.popupMode = 'upload';
      this.showUploadInstructionsModal = true;
    },
    
    async processUploadExternal() {
      if (!this.uploadedFile || !this.selectedTemplate) {
        return;
      }
      
      try {
        const formData = new FormData();
        formData.append('csvFile', this.uploadedFile);
        formData.append('templateId', this.selectedTemplate.id);
        formData.append('legalEntityId', this.leId);
        
        await this.uploadCsv(formData);
        this.showUploadConfirmModal = false;
        if (this.uploadResponse && this.uploadResponse.statusCode === 400) {
          this.successPopupMessage = this.uploadResponse.message;
          this.successPopupType = 'Warning';
          this.showSuccessPopup = true;
        } else {
          let message = this.uploadResponse.success ? `Upload completed successfully\n`:  `Upload failed\n`;
          if (this.uploadResponse.updated > 0) {
            message += `${'\n'}Updated: ${this.uploadResponse.updated} records\n`;
          } else if(this.uploadResponse.updated === 0) {
            message += `\n Updated ${this.uploadResponse.updated} records\n`;
          }
          if(this.uploadResponse.new > 0) {
            message += `${'\n'}New: ${this.uploadResponse.new} records\n`;
          } else if(this.uploadResponse.new === 0) {
            message += `\n New ${this.uploadResponse.new} records\n`;
          }
          // Handle blob errors for download
          if (this.uploadResponse.errors) {
            const errorBlob = atob(this.uploadResponse.errors);
            this.logSheetUrl = window.URL.createObjectURL(new Blob([errorBlob], { type: 'text/plain' }));
          }
          
          this.successPopupMessage = message;
          this.successPopupType = (this.uploadResponse.validationErrors && this.uploadResponse.validationErrors.length > 0) || this.uploadResponse.errors ? 'Warning' : 'Success';
          this.showSuccessPopup = true;
          
          await this.getTemplateDetails({
            templateId: this.selectedTemplate.id,
            leId: this.leId
          });
        }
        
        // Clear the uploaded file
        this.uploadedFile = null;
        this.$refs.csvUploadRef.del();
      } catch (error) {
        console.error('Error uploading CSV:', error);
      }
    },
    
    showDownloadTemplatePopup() {
      if (!this.selectedTemplate) {
        return;
      }
      this.popupMode = 'download';
      this.showUploadInstructionsModal = true;
    },
    
    closeUploadInstructionsPopup() {
      this.showUploadInstructionsModal = false;
    },
    
    showUploadConfirmation() {
      if (!this.uploadedFile || !this.selectedTemplate) {
        return;
      }
      this.showUploadConfirmModal = true;
    },
    
    async confirmUpload() {
      try {
        this.isConfirmingUpload = true;
        await this.processUploadExternal();
      } finally {
        this.isConfirmingUpload = false;
      }
    },
    
    async processCsvUpload() {
      try {
        const formData = new FormData();
        formData.append('csvFile', this.uploadedFile);
        formData.append('templateId', this.selectedTemplate.id);
        formData.append('legalEntityId', this.leId );
        
        await this.uploadCsv(formData);
        
        console.log('Upload response after API call:', this.uploadResponse);
        if (this.uploadResponse && this.uploadResponse.statusCode === 400) {
          console.log('Upload validation failed:', this.uploadResponse);

          this.successPopupMessage = this.uploadResponse.message;
          this.successPopupType = 'Warning';
          this.showUploadConfirmModal = false;
          this.showSuccessPopup = true;
          console.log('Setting showSuccessPopup to true:', this.showSuccessPopup);
          this.uploadedFile = null;
          this.$refs.csvUploadRef.del();
          return;
        } else {
          // Success case - close modal and refresh data
          this.showUploadConfirmModal = false;
          
          // Build success message with updated and errors info
          let message = `Upload completed successfully!\n`;
          if (this.uploadResponse.updated > 0) {
            message += `${'\n'}Updated: ${this.uploadResponse.updated} records\n`;
          }
          if (this.uploadResponse.errors && this.uploadResponse.errors.length > 0) {
            message += `Errors:\n${this.uploadResponse.errors.join('\n')}`;
          }
          
          this.successPopupMessage = message;
          this.successPopupType = this.uploadResponse.errors && this.uploadResponse.errors.length > 0 ? 'Warning' : 'Success';
          this.showSuccessPopup = true;
          
          await this.getTemplateDetails({
            templateId: this.selectedTemplate.id,
            leId: this.leId
          });
          
          this.uploadedFile = null;
          this.$refs.csvUploadRef.del();
        }
      } catch (error) {
        console.error('Error uploading CSV:', error);
      }
    },
    
    async loadGenerateLettersData() {
      if (this.templateDetails && this.templateDetails.requests) {
        this.generateLettersData = this.templateDetails.requests;
      }
    },
    
    async refreshTemplateDetails() {
      if (this.selectedTemplate && this.selectedTemplate.id) {
        await this.getTemplateDetails({
          templateId: this.selectedTemplate.id,
          leId: this.leId
        });
      }
    },
    
    onRequestsSelected(selectedRequests) {
      console.log('Selected requests:', selectedRequests);
      this.selectedRequests = selectedRequests;
      this.processSelectedLettersToggle = selectedRequests.length > 0;
    },
    
    onHeaderSelection(isSelected, selectedRecords) {
      console.log('Header selection triggered:', isSelected, selectedRecords);
      this.selectedRequests = selectedRecords;
      this.processSelectedLettersToggle = selectedRecords.length > 0;
      console.log('Selected requests count:', this.selectedRequests.length);
    },
    
    showProcessSelectedConfirmation() {
      if (this.selectedRequests.length === 0) {
        this.showToast('Please select requests to process', 'warning');
        return;
      }
      this.showProcessSelectedConfirmModal = true;
    },
    
    async confirmProcessSelectedRequests() {
      this.isConfirmingProcessSelected = true;
      try {
        await this.processSelectedRequests();
      } finally {
        this.isConfirmingProcessSelected = false;
        this.showProcessSelectedConfirmModal = false;
      }
    },
    
    async processSelectedRequests() {
      try {
        const payload = {
          requestIds: this.selectedRequests.map(req => req.id)
        };
        
        const response = await this.processSelectedLetters(payload);
        
        this.showProcessSelectedConfirmModal = false;
        let message = `${response.data.message}, \n`
        if(response.data.estimatedProcessingTime){
          message += `estimated processing time: ${response.data.estimatedProcessingTime}`; 
        }
        if (response.data.failedBatches > 0) {
          message += `\nFailed: ${response.data.failedBatches} failedBatches`;
        }
        this.successPopupMessage = message;
        this.successPopupType = response.data.failedBatches > 0 ? 'Warning' : 'Success';
        this.showSuccessPopup = true;
        await this.refreshTemplateDetails();
      } catch (error) {
        console.error('Error processing selected requests:', error);
      }
    },
    
    async processRequest(request) {
      try {
        const response = await this.processLetter(request.id);
        
        this.showProcessConfirmModal = false;
        this.successPopupMessage = `${response.data.processedCount} ${response.data.message} ` || 'Request processed successfully' 
        this.successPopupType = 'Success';
        this.showSuccessPopup = true;
        await this.refreshTemplateDetails();
      } catch (error) {
        console.error('Error processing request:', error);
      }
    },
    
    showDeleteConfirmation(request) {
      this.selectedRequestToDelete = request;
      this.showDeleteConfirmModal = true;
    },
    
    async confirmDeleteRequest() {
      this.isConfirmingDelete = true;
      try {
        if (this.selectedRequestToDelete) {
          await this.handleDeleteRequest(this.selectedRequestToDelete);
          this.selectedRequestToDelete = null;
        }
      } finally {
        this.isConfirmingDelete = false;
        this.showDeleteConfirmModal = false;
      }
    },
    
    async handleDeleteRequest(request) {
      try {
        const response = await this.deleteRequest(request.id);
        this.showDeleteConfirmModal = false;
        this.successPopupMessage = `${response.data.message} ` || 'Request deleted successfully';
        this.successPopupType = 'Success';
        this.showSuccessPopup = true;
        await this.refreshTemplateDetails();
      } catch (error) {
        console.error('Error deleting request:', error);
      }
    },
    
    async fetchBulkLetters() {
      try {
        const payload = {
          legalEntityId:  this.leId , // this.leId,
          year: this.bulkSelectedYear && this.bulkSelectedYear.value ? parseInt(this.bulkSelectedYear.value) : null,
          templateId: this.bulkSelectedTemplate && this.bulkSelectedTemplate.id ? this.bulkSelectedTemplate.id : null,
          employeeCodes: this.bulkSearchEmployee && this.bulkSearchEmployee.trim() ? this.bulkSearchEmployee : null
        };
        
        const response = await this.bulkDownload(payload);
        
        if (this.bulkDownloadResponse && this.bulkDownloadResponse.downloadUrls) {
          this.showToast(`Found ${this.bulkDownloadResponse.downloadUrls.length} letters`, 'success');
        } else {
          this.showToast('No letters found for the selected criteria', 'info');
        }
      } catch (error) {
        console.error('Error fetching letters:', error);
      }
    },
    

    
    async downloadZip() {
      try {
        const payload = {
          legalEntityId: this.leId,
          year: this.bulkSelectedYear && this.bulkSelectedYear.value ? parseInt(this.bulkSelectedYear.value) : null,
          templateId: this.bulkSelectedTemplate && this.bulkSelectedTemplate.id ? this.bulkSelectedTemplate.id : null,
          employeeCodes: this.bulkSearchEmployee && this.bulkSearchEmployee.trim() ? this.bulkSearchEmployee : null
        };
        
        const response = await this.bulkDownloadZip(payload);
        console.log('Response from bulkDownloadZip:', response)
        if (response && response.data) {
          const blob = new Blob([response.data], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `letters_${new Date().toISOString().slice(0, 10)}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          this.showToast('ZIP file downloaded successfully', 'success');
        }
      } catch (error) {
        console.error('Error downloading ZIP manjnath', error);
        const errorMessage = error.error || `failed to create ZIP file: \n No letters found for the specified criteria`
        this.successPopupMessage = errorMessage;
        this.successPopupType = 'Warning';
        this.showSuccessPopup = true;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    
    // getTemplateName(templateId) {
    //   const template = this.templates.find(t => t.id === templateId);
    //   return template ? template.display_name : 'Unknown Template';
    // },
    
    showToast(message, type = 'success') {
      this.toastMessage = {
        show: true,
        text: message,
        type: type
      };
    },
    
    closeToast() {
      this.toastMessage.show = false;
    },
    
    async downloadSingleFile(record) {
      if (!record.downloadUrl) {
        return;
      }

      const fileId = record.id;
      const filename = `${record.employeeCode}_${record.templateCode || 'letter'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      
      this.$set(this.downloadingFiles, fileId, true);
      this.$set(this.downloadProgress, fileId, 'Starting...');
      
      try {
        const response = await fetch(record.downloadUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        console.error('Download error:', error);
      } finally {
        this.$set(this.downloadingFiles, fileId, false);
        this.$delete(this.downloadProgress, fileId);
      }
    },
    

    

    
    showZipConfirmation() {
      this.showZipConfirmModal = true;
    },
    
    async confirmZipDownload() {
      this.isConfirmingZipDownload = true;
      try {
        await this.downloadZip();
      } finally {
        this.isConfirmingZipDownload = false;
        this.showZipConfirmModal = false;
      }
    },
    
    showProcessConfirmation(request) {
      this.selectedRequestToProcess = request;
      this.showProcessConfirmModal = true;
    },
    
    async confirmProcessRequest() {
      this.isConfirmingProcess = true;
      try {
        if (this.selectedRequestToProcess) {
          await this.processRequest(this.selectedRequestToProcess);
          this.selectedRequestToProcess = null;
        }
      } catch(err) {
        console.log(err)
      } finally {
        this.isConfirmingProcess = false;
        this.showProcessConfirmModal = false;
      }
    },
    
    closeSuccessPopup() {
      this.showSuccessPopup = false;
      // Clean up error download URL
      if (this.logSheetUrl) {
        window.URL.revokeObjectURL(this.logSheetUrl);
        this.logSheetUrl = null;
      }
      // Close upload modal if it was a validation error
      if (this.uploadResponse && this.uploadResponse.statusCode === 400) {
        this.showUploadConfirmModal = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/scss/components/margins.scss";

.main-container {
  padding: 20px;
}

.menu-tabs {
  display: flex;
  border-bottom: 2px solid #efefef;
  margin-bottom: 24px;
}

.menu-tab {
  padding: 8px 2px;
  background: none;
  border: none;
  font-size: 16px;
  font-weight: normal;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-right: 32px;
  transition: all 0.3s ease;
  
  &:hover {
    color: #5d409f;
  }
  
  &.active {
    color: #5d409f;
    font-weight: 600;
    border-bottom: 2px solid #5d409f;
  }
}

.tab-content {
  min-height: 400px;
}

.filter-container {
  background-color: #ffffff;
  padding: 24px;
  margin-bottom: 24px;
}

.filter-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 20px;
  align-items: end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.template-actions {
  background-color: #ffffff;
  padding: 24px;
}

.action-row {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 0
}

.csv-download-link {
  margin-top: 10px;
}

.download-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #34c0be;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border: 1px solid #34c0be;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f0fffe;
    color: #2a9d9b;
    border-color: #2a9d9b;
  }
  
  .download-icon {
    width: 16px;
    height: 16px;
  }
}

.upload-section {
  flex: 1;
  max-width: 300px;
}

.filter-item {
  min-width: 0;
}

.input-label {
  color: #000000;
  font-size: 14px;
  margin-bottom: 5px;
  font-weight: 400;
}

.template-loading {
  .input-label {
    display: block;
    margin-bottom: 8px;
  }
}

.filter-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.results-container {
  background-color: #ffffff;
  border-radius: 8px;
  border: solid 1px #efefef;
  padding: 24px;
  margin-top: 10px;
}

.results-header {
  border-bottom: 1px solid #efefef;
  padding-bottom: 16px;
}

.results-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
}

.results-title-info {
  flex: 1;
}

.zip-download-btn {
  flex-shrink: 0;
  align-self: flex-start;
}

.download-container {
  display: flex;
  align-items: center;
  justify-content: start;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: start;
  background: transparent;
  color: #34c0be;
  border: none;
  border-radius: 4px;
  // padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
  justify-content: center;
  
  &:hover:not(:disabled) {
    color: #2a9d9b;
  }
  
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
}

.download-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.download-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    font-size: 12px;
  }
}

.download-icon {
  width: 16px;
  height: 16px;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}



.filter-item ::v-deep .custom-select {
  .vs__dropdown-toggle {
    min-height: 35px !important;
    max-height: 35px !important;
    min-width: 200px !important;
  }
  
  .vs__selected {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
  
  .vs__dropdown-option {
    white-space: nowrap;
    overflow:visible;
    text-overflow: ellipsis;
    max-width: max-content;
  }
}

::v-deep .filter-year .vs__dropdown-menu ul {
    cursor: pointer;
  }

::v-deep .filter-year  .vs__dropdown-menu li {
    background-color: transparent !important;
    padding-right:250px !important;
  }
.results-info {
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
}

.letters-table {
  margin-top: 16px;
}

.table-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.table-btn {
  padding: 4px 8px !important;
  font-size: 12px !important;
  min-height: 28px !important;
  min-width: 60px !important;
  white-space: nowrap;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  
  &.uploaded {
    background-color: #e3f2fd;
    color: #1976d2;
  }
  
  &.processed {
    background-color: #e8f5e8;
    color: #2e7d32;
  }
  
  &.failed {
    background-color: #ffebee;
    color: #c62828;
  }
}

.action-buttons {
  display: flex;
  gap: 8px;
  
  .btn {
    padding: 4px 12px;
    font-size: 12px;
  }
}

.employee-info {
  .employee-id {
    font-weight: 600;
    color: #333;
  }
  
  .employee-name {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
  }
}

.template-info {
  font-weight: 500;
  color: #444;
}

.generated-date {
  color: #666;
  font-size: 14px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  
  .empty-state-content {
    text-align: center;
    max-width: 400px;
  }
}

.no-records-message {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

// Responsive design
@media (max-width: 1024px) {
  .filter-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .main-container {
    padding: 16px;
  }
  
  .filter-container,
  .results-container {
    padding: 16px;
  }
}

// Upload Instructions Popup Styles

.upload-instructions-popup ::v-deep .modal {
  // width: 500px;
}
.upload-instructions-popup ::v-deep .modal-content {
  // align-items: stretch !important;
  
  .popup-btns{
    margin-left: auto;
    margin-right: auto;
  }
  @media (max-width: 430px) {
    width: 500px !important;
    overflow-x: scroll;
  }
  @media (max-width: 1400px) {
     width: 50% !important;
  } 
  
  @media (max-width: 1024px) {
    width: 60% !important;
  }
  
  @media (max-width: 768px) {
    width: 90% !important;
  }
}

.upload-instructions-content {
  // width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  .instructions-section {
    margin-bottom: 24px;
    max-height: 300px;
    overflow-y: auto;
    width: 100%;
    
    .instruction-group {
      margin-bottom: 20px;
      
      h4 {
        color: #333;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 8px;
        border-bottom: 1px solid #eee;
        padding-bottom: 4px;
      }
      
      .instruction-list {
        ul {
          margin: 8px 0;
          padding-left: 20px;
          
          li {
            margin-bottom: 4px;
            color: #555;
            font-size: 14px;
          }
        }
        
        p {
          margin: 8px 0;
          color: #666;
          font-size: 14px;
          // font-style: italic;
        }
      }
      
      .instruction-text {
        p {
          margin: 8px 0;
          color: #555;
          font-size: 14px;
        }
      }
    }
  }
  
  .upload-section-popup {
    margin: 20px 0;
    padding: 16px;
    border: 2px dashed #ddd;
    border-radius: 8px;
    background-color: #fafafa;
  }
  
  .popup-btn-group {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
    
    .btn-popup,
    .btn-popup-secondary {
      min-width: 120px;
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
    
    .btn-popup {
      background-color: #34c0be;
      color: white;
      border: none;
      
      &:hover:not(:disabled) {
        background-color: #2a9d9b;
      }
    }
    
    .btn-popup-secondary {
      background-color: white;
      color: #34c0be;
      border: 2px solid #34c0be;
      
      &:hover:not(:disabled) {
        background-color: #f0fffe;
      }
    }
    
    .download-template-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      text-decoration: none;
      
      .download-icon {
        width: 16px;
        height: 16px;
      }
    }
  }
}

// Error Log Download Styles
.custom-container {
  display: flex;
  align-items: center;
  gap: 8px;
  
  a {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #34c0be;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      color: #2a9d9b;
    }
    
    .img {
      width: 16px;
      height: 16px;
    }
    
    .text {
      font-size: 14px;
    }
  }
}

.btn-popup {
  background-color: #34c0be;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #2a9d9b;
  }
}

</style>